import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    CircularProgress,
    Snackbar,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    LinearProgress,
    Tabs,
    Tab,
    Paper,
    Select,
    FormControl,
    InputLabel,
    MenuItem
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { DeleteSweep, Refresh, AutoDelete } from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';

// Define TS Interface for User
interface CleanupUser {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    order_count: number;
    date_created: string | null;
    last_login: string | null;
    avatar_url: string | null;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

export default function CleanUpUsers() {
    const { enqueueSnackbar } = useSnackbar();
    const [tabValue, setTabValue] = useState(0);

    // Tab 1: No Phone + No Orders
    const [usersTab1, setUsersTab1] = useState<CleanupUser[]>([]);
    const [loadingTab1, setLoadingTab1] = useState<boolean>(false);
    const [deletingTab1, setDeletingTab1] = useState<boolean>(false);
    const [rowSelectionTab1, setRowSelectionTab1] = useState<GridRowSelectionModel>([]);

    // Tab 2: Has Phone + No Orders
    const [usersTab2, setUsersTab2] = useState<CleanupUser[]>([]);
    const [loadingTab2, setLoadingTab2] = useState<boolean>(false);
    const [deletingTab2, setDeletingTab2] = useState<boolean>(false);
    const [rowSelectionTab2, setRowSelectionTab2] = useState<GridRowSelectionModel>([]);

    // Tab 3: Advanced Filter
    const [usersTab3, setUsersTab3] = useState<CleanupUser[]>([]);
    const [loadingTab3, setLoadingTab3] = useState<boolean>(false);
    const [deletingTab3, setDeletingTab3] = useState<boolean>(false);
    const [rowSelectionTab3, setRowSelectionTab3] = useState<GridRowSelectionModel>([]);

    // Advanced filter parameters
    const [filterUserType, setFilterUserType] = useState<string>('');
    const [filterHasPhone, setFilterHasPhone] = useState<string>('');
    const [filterHasAddress, setFilterHasAddress] = useState<string>('');
    const [filterHasOrders, setFilterHasOrders] = useState<string>('');
    const [filterHasLastLogin, setFilterHasLastLogin] = useState<string>('');

    const [autoCleaning, setAutoCleaning] = useState<boolean>(false);
    const [autoCleanupResult, setAutoCleanupResult] = useState<any>(null);

    // Dialog state
    const [openAutoDialog, setOpenAutoDialog] = useState(false);

    const fetchUsers = async (filterType: 'no_phone_no_orders' | 'has_phone_no_orders') => {
        const isTab1 = filterType === 'no_phone_no_orders';
        const setLoading = isTab1 ? setLoadingTab1 : setLoadingTab2;
        const setUsers = isTab1 ? setUsersTab1 : setUsersTab2;
        const setRowSelection = isTab1 ? setRowSelectionTab1 : setRowSelectionTab2;

        setLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/cleanup-users?limit=100&filter_type=${filterType}`,
                { withCredentials: true }
            );
            setUsers(response.data);
            // Auto-select all by default
            setRowSelection(response.data.map((u: CleanupUser) => u.id));
        } catch (error) {
            console.error("Error fetching users:", error);
            enqueueSnackbar('Failed to fetch users', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Load both tabs on mount
        fetchUsers('no_phone_no_orders');
        fetchUsers('has_phone_no_orders');
    }, []);

    const handleBulkDelete = async (filterType: 'no_phone_no_orders' | 'has_phone_no_orders') => {
        const isTab1 = filterType === 'no_phone_no_orders';
        const rowSelection = isTab1 ? rowSelectionTab1 : rowSelectionTab2;
        const setDeleting = isTab1 ? setDeletingTab1 : setDeletingTab2;

        if (rowSelection.length === 0) return;

        if (!confirm(`Are you sure you want to permanently delete ${rowSelection.length} users? This action cannot be undone.`)) {
            return;
        }

        setDeleting(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/cleanup-users/delete`,
                { user_ids: rowSelection },
                { withCredentials: true }
            );

            enqueueSnackbar(`Successfully deleted ${response.data.deleted_count} users`, { variant: 'success' });

            // Refresh both tabs
            fetchUsers('no_phone_no_orders');
            fetchUsers('has_phone_no_orders');
        } catch (error) {
            console.error("Error deleting users:", error);
            enqueueSnackbar('Failed to delete users', { variant: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const handleAutoCleanup = async () => {
        setAutoCleaning(true);
        setAutoCleanupResult(null);
        try {
            const response = await axios.post(`${API_BASE_URL}/cleanup-users/auto-cleanup`, {}, { withCredentials: true });
            setAutoCleanupResult(response.data);
        } catch (error) {
            console.error("Auto cleanup failed:", error);
            setAutoCleanupResult({
                success: false,
                message: "Failed to perform auto-cleanup. Check console."
            });
        } finally {
            setAutoCleaning(false);
            // Refresh both tabs
            fetchUsers('no_phone_no_orders');
            fetchUsers('has_phone_no_orders');
        }
    };

    const handleAdvancedSearch = async () => {
        setLoadingTab3(true);
        try {
            const params = new URLSearchParams();
            params.append('limit', '100');

            // Add filters only if they have values
            if (filterUserType) {
                params.append('user_type', filterUserType);
            }
            if (filterHasPhone) {
                params.append('has_phone', filterHasPhone === 'true' ? 'true' : 'false');
            }
            if (filterHasAddress) {
                params.append('has_address', filterHasAddress === 'true' ? 'true' : 'false');
            }
            if (filterHasOrders) {
                params.append('has_orders', filterHasOrders === 'true' ? 'true' : 'false');
            }
            if (filterHasLastLogin) {
                params.append('has_last_login', filterHasLastLogin === 'true' ? 'true' : 'false');
            }

            const response = await axios.get(
                `${API_BASE_URL}/cleanup-users/advanced?${params.toString()}`,
                { withCredentials: true }
            );

            setUsersTab3(response.data);
            // Auto-select all by default
            setRowSelectionTab3(response.data.map((u: CleanupUser) => u.id));
            enqueueSnackbar(`Found ${response.data.length} users matching filters`, { variant: 'success' });
        } catch (error) {
            console.error("Error fetching filtered users:", error);
            enqueueSnackbar('Failed to fetch filtered users', { variant: 'error' });
        } finally {
            setLoadingTab3(false);
        }
    };

    const handleClearFilters = () => {
        setFilterUserType('');
        setFilterHasPhone('');
        setFilterHasAddress('');
        setFilterHasOrders('');
        setFilterHasLastLogin('');
        setUsersTab3([]);
        setRowSelectionTab3([]);
    };

    const handleBulkDeleteTab3 = async () => {
        if (rowSelectionTab3.length === 0) return;

        if (!confirm(`Are you sure you want to permanently delete ${rowSelectionTab3.length} users? This action cannot be undone.`)) {
            return;
        }

        setDeletingTab3(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/cleanup-users/delete`,
                { user_ids: rowSelectionTab3 },
                { withCredentials: true }
            );

            enqueueSnackbar(`Successfully deleted ${response.data.deleted_count} users`, { variant: 'success' });

            // Refresh all tabs
            fetchUsers('no_phone_no_orders');
            fetchUsers('has_phone_no_orders');
            handleAdvancedSearch(); // Re-run the search
        } catch (error) {
            console.error("Error deleting users:", error);
            enqueueSnackbar('Failed to delete users', { variant: 'error' });
        } finally {
            setDeletingTab3(false);
        }
    };

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 90 },
        {
            field: 'name',
            headerName: 'Name',
            width: 200,
            valueGetter: (params) => `${params.row.first_name} ${params.row.last_name}`
        },
        { field: 'email', headerName: 'Email', width: 250 },
        {
            field: 'phone',
            headerName: 'Phone',
            width: 150,
            renderCell: (params) => (
                params.value ? params.value : <Chip label="No Phone" color="warning" size="small" />
            )
        },
        {
            field: 'order_count',
            headerName: 'Orders',
            width: 100,
            align: 'center',
            renderCell: (params) => (
                <Chip label={params.value} color={params.value === 0 ? "error" : "success"} size="small" variant="outlined" />
            )
        },
        {
            field: 'date_created',
            headerName: 'Created At',
            width: 200,
            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : '-'
        },
    ];

    const renderTabContent = (filterType: 'no_phone_no_orders' | 'has_phone_no_orders', tabIndex: number) => {
        const isTab1 = filterType === 'no_phone_no_orders';
        const users = isTab1 ? usersTab1 : usersTab2;
        const loading = isTab1 ? loadingTab1 : loadingTab2;
        const deleting = isTab1 ? deletingTab1 : deletingTab2;
        const rowSelection = isTab1 ? rowSelectionTab1 : rowSelectionTab2;
        const setRowSelection = isTab1 ? setRowSelectionTab1 : setRowSelectionTab2;

        const description = isTab1
            ? 'Showing users with No Phone Number and No Order History.'
            : 'Showing users with Phone Numbers but No Order History.';

        return (
            <TabPanel value={tabValue} index={tabIndex}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                    <Box>
                        <Button
                            startIcon={<Refresh />}
                            onClick={() => fetchUsers(filterType)}
                            variant="outlined"
                            sx={{ mr: 2 }}
                            disabled={loading || deleting || autoCleaning}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteSweep />}
                            onClick={() => handleBulkDelete(filterType)}
                            disabled={loading || deleting || rowSelection.length === 0}
                        >
                            Delete Selected ({rowSelection.length})
                        </Button>
                    </Box>
                </Box>

                <DataGrid
                    rows={users}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 100 },
                        },
                    }}
                    pageSizeOptions={[50, 100]}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        setRowSelection(newRowSelectionModel);
                    }}
                    rowSelectionModel={rowSelection}
                    loading={loading}
                    autoHeight
                    sx={{ minHeight: 400 }}
                />
            </TabPanel>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Clean Up Inactive Users
                </Typography>
                <Button
                    variant="contained"
                    color="warning"
                    startIcon={<AutoDelete />}
                    onClick={() => setOpenAutoDialog(true)}
                    disabled={loadingTab1 || loadingTab2 || loadingTab3 || deletingTab1 || deletingTab2 || deletingTab3 || autoCleaning}
                >
                    One-Click Auto Clean
                </Button>
            </Box>

            <Paper>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                    <Tab label={`No Numbers and Order (${usersTab1.length})`} />
                    <Tab label={`Number with No Orders (${usersTab2.length})`} />
                    <Tab label={`Advanced Filter (${usersTab3.length})`} />
                </Tabs>

                {renderTabContent('no_phone_no_orders', 0)}
                {renderTabContent('has_phone_no_orders', 1)}

                {/* Tab 3: Advanced Filter */}
                <TabPanel value={tabValue} index={2}>
                    <Grid container spacing={3}>
                        {/* Filter Form */}
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Filter Criteria
                                    </Typography>

                                    <FormControl fullWidth sx={{ mt: 2 }}>
                                        <InputLabel>User Type</InputLabel>
                                        <Select
                                            value={filterUserType}
                                            label="User Type"
                                            onChange={(e) => setFilterUserType(e.target.value)}
                                        >
                                            <MenuItem value="">Any</MenuItem>
                                            <MenuItem value="customer">Customer</MenuItem>
                                            <MenuItem value="subscriber">Subscriber</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mt: 2 }}>
                                        <InputLabel>Mobile Number</InputLabel>
                                        <Select
                                            value={filterHasPhone}
                                            label="Mobile Number"
                                            onChange={(e) => setFilterHasPhone(e.target.value)}
                                        >
                                            <MenuItem value="">Any</MenuItem>
                                            <MenuItem value="true">Yes</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mt: 2 }}>
                                        <InputLabel>Delivery/Billing Address</InputLabel>
                                        <Select
                                            value={filterHasAddress}
                                            label="Delivery/Billing Address"
                                            onChange={(e) => setFilterHasAddress(e.target.value)}
                                        >
                                            <MenuItem value="">Any</MenuItem>
                                            <MenuItem value="true">Yes</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mt: 2 }}>
                                        <InputLabel>Has Orders</InputLabel>
                                        <Select
                                            value={filterHasOrders}
                                            label="Has Orders"
                                            onChange={(e) => setFilterHasOrders(e.target.value)}
                                        >
                                            <MenuItem value="">Any</MenuItem>
                                            <MenuItem value="true">Yes</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ mt: 2 }}>
                                        <InputLabel>Last Login</InputLabel>
                                        <Select
                                            value={filterHasLastLogin}
                                            label="Last Login"
                                            onChange={(e) => setFilterHasLastLogin(e.target.value)}
                                        >
                                            <MenuItem value="">Any</MenuItem>
                                            <MenuItem value="true">Has Value</MenuItem>
                                            <MenuItem value="false">No Value</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={handleAdvancedSearch}
                                            disabled={loadingTab3}
                                            startIcon={loadingTab3 ? <CircularProgress size={20} color="inherit" /> : null}
                                        >
                                            {loadingTab3 ? 'Searching...' : 'Run Search'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={handleClearFilters}
                                            disabled={loadingTab3}
                                        >
                                            Clear
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Results Table */}
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {usersTab3.length > 0
                                        ? `Showing ${usersTab3.length} users matching your filters`
                                        : 'Select filters and click "Run Search" to find users'}
                                </Typography>
                                {usersTab3.length > 0 && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={deletingTab3 ? <CircularProgress size={20} color="inherit" /> : <DeleteSweep />}
                                        onClick={handleBulkDeleteTab3}
                                        disabled={loadingTab3 || deletingTab3 || rowSelectionTab3.length === 0}
                                    >
                                        Delete Selected ({rowSelectionTab3.length})
                                    </Button>
                                )}
                            </Box>

                            <DataGrid
                                rows={usersTab3}
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { page: 0, pageSize: 100 },
                                    },
                                }}
                                pageSizeOptions={[50, 100]}
                                checkboxSelection
                                onRowSelectionModelChange={(newRowSelectionModel) => {
                                    setRowSelectionTab3(newRowSelectionModel);
                                }}
                                rowSelectionModel={rowSelectionTab3}
                                loading={loadingTab3}
                                autoHeight
                                sx={{ minHeight: 400 }}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>
            </Paper>

            {/* Auto Cleanup Dialog */}
            <Dialog
                open={openAutoDialog}
                onClose={() => !autoCleaning && setOpenAutoDialog(false)}
            >
                <DialogTitle>One-Click Auto Cleanup</DialogTitle>
                <DialogContent>
                    {!autoCleanupResult ? (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>
                                This will <strong>scan all users (up to 10,000)</strong>, identify those with <strong>No Phone + No Orders</strong>, and <strong>automatically delete them</strong>.
                                <br /><br />
                                This process may take 30-60 seconds. Do not close this window.
                            </DialogContentText>
                            {autoCleaning && <LinearProgress />}
                        </>
                    ) : (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="success">
                                {autoCleanupResult.message}
                            </Alert>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Scanned: {autoCleanupResult.scanned_count} <br />
                                Deleted: {autoCleanupResult.deleted_count}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {!autoCleaning && !autoCleanupResult && (
                        <>
                            <Button onClick={() => setOpenAutoDialog(false)}>Cancel</Button>
                            <Button onClick={handleAutoCleanup} variant="contained" color="error" autoFocus>
                                Start Auto Cleanup
                            </Button>
                        </>
                    )}
                    {!autoCleaning && autoCleanupResult && (
                        <Button onClick={() => setOpenAutoDialog(false)} autoFocus>
                            Close
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
